import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import get from 'lodash.get';
import { Typography, FlexBox, Button, color, Switch, MuiBox, SvgIcon, Table, Card } from '@kis-technologies/kis-ui';
import { Kiosk, HardwareTypes, KioskHardware } from 'services/kiosk';
import { formatHardwareTitle, orDash } from 'utils/stringFormat';
import { OPERATIONAL, CONNECTED, USER_ERROR, NO_PING } from 'constants/hardware';
import { selectors as hardwareSelectors, actions as hardwareActions, Printer } from 'services/hardware';
import { selectors as kioskSelectors, TicketTemplate } from 'services/kiosk';
import { useSelector } from 'react-redux';
import { useActions } from 'hooks/useActions';
import { useStyles } from '../styled';
import { ActionOverlay } from 'components/ActionOverlay';
import { TicketStockOverlay } from 'components/TicketStockOverlay';
import { Scrollbar } from 'components/Scrollbar';

export type KioskHardwareProps = {
  loading: boolean;
  statusChange: () => void;
};

export const HardwareTable = (props: KioskHardwareProps) => {
  const navigate = useNavigate();
  // Actions Creators and Selectors
  const scanner = useSelector(hardwareSelectors.selectScanner);
  const payment = useSelector(hardwareSelectors.selectPaymentDevice);
  const hardwarePrinters = useSelector(hardwareSelectors.selectPrinters);
  const kiosk = useSelector(kioskSelectors.selectKiosk) as Kiosk;
  const kioskScanner = useSelector(kioskSelectors.selectScannerHardware) as KioskHardware;
  const kioskPayment = useSelector(kioskSelectors.selectPaymentHardware) as KioskHardware;

  const { pingPrinters, scannerPing, paymentPing, setPrintData, scannerRead, resetScanner } =
    useActions(hardwareActions);

  const [hardwareTrouble, setHardwareTrouble] = React.useState<KioskHardware | null>(null);
  const [hardwareDevices, setHardwareDevices] = React.useState<KioskHardware[]>([]);
  const [pingPrintersCompleted, setPingPrintersCompleted] = React.useState<boolean>(false);
  const [kioskHardwareIdForRestock, setKioskHardwareIdForRestock] = React.useState<string | null>(null);

  const classes = useStyles();

  // Ping the scanner and payment device on load
  useEffect(() => {
    if (kioskScanner) scannerPing();
    if (kioskPayment) paymentPing();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ping the printer(s) after they've been registered in the hardware store
  useEffect(() => {
    if (hardwarePrinters.length && !pingPrintersCompleted) {
      pingPrinters({});
      setPingPrintersCompleted(true);
    }
  }, [hardwarePrinters]);

  useEffect(() => {
    const hardwares = kiosk.hardware.slice(0);
    setHardwareDevices(hardwares);
  }, [kiosk.hardware]);

  // ***************
  // ** FUNCTIONS **
  // ***************

  if (scanner.status.message) setTimeout(resetScanner, 5000);

  // Set the print data and re-route to the printing page (PrintingView)
  const testPrint = (message: string, kioskHardwareId: string) => {
    setPrintData({ printJobs: [{ message, kioskHardwareId }] });

    navigate('/maintenance/printing');
  };

  const onRetestHardware = () => {
    if (hardwareTrouble) {
      switch (hardwareTrouble.type) {
        case 'barcodeScanner':
          scannerPing(true);
          break;
        case 'paymentDevice':
          paymentPing(true);
          break;
        case 'printer':
          pingPrinters({ kioskHardwareId: hardwareTrouble.id, userInitiated: true });
          break;
        default:
          break;
      }
    }
    setHardwareTrouble(null);
  };

  const HardwareStatus = ({ type, kioskHardwareId }: { type: HardwareTypes; kioskHardwareId: string }) => {
    if (checkLoading(type, kioskHardwareId)) {
      return (
        <Button appearance="white" isLoading>
          Loading
        </Button>
      );
    }
    const status = checkStatus(type, kioskHardwareId);
    if (status === 'Active') {
      return <SvgIcon name="success_circle" color={color.GREEN} />;
    } else if (status === 'Inactive') {
      return <SvgIcon name="error_circle" color={color.RED} />;
    }
    return null;
  };

  const checkLoading = (type: string, kioskHardwareId: string): boolean => {
    if (type === 'printer') {
      const hardwarePrinter = hardwarePrinters.find((p) => p.kioskHardwareId === kioskHardwareId);
      return hardwarePrinter && hardwarePrinter.loading ? true : false;
    }

    return (type === 'barcodeScanner' && scanner.loading) || (type === 'paymentDevice' && payment.loading);
  };

  const checkStatus = (type: string, kioskHardwareId: string) => {
    let status = false;
    let statusCode = '';
    if (type === 'barcodeScanner') {
      status = [OPERATIONAL, CONNECTED, USER_ERROR].includes(scanner.status.code);
      statusCode = scanner.status.code;
    } else if (type === 'paymentDevice') {
      status = payment.status.code === OPERATIONAL;
      statusCode = payment.status.code;
    } else if (type === 'printer') {
      const hardwarePrinter = hardwarePrinters.find((p) => p.kioskHardwareId === kioskHardwareId);
      if (hardwarePrinter) {
        status = [OPERATIONAL, CONNECTED].includes((hardwarePrinter as Printer).status.code);
        statusCode = (hardwarePrinter as Printer).status.code;
      }
    }
    if (NO_PING === statusCode) {
      return NO_PING;
    }
    return status ? 'Active' : 'Inactive';
  };

  const getHardwareDisplayName = (kioskHardware: KioskHardware) => {
    const { type, configuration } = kioskHardware;

    if (type === 'printer' && configuration.displayName) {
      return `${configuration.displayName} ${formatHardwareTitle(type)}`;
    }

    return formatHardwareTitle(type);
  };

  const HardwareActionsColumn = ({ kioskHardware }: { kioskHardware: KioskHardware }) => {
    const { id, type, configuration, values } = kioskHardware;

    if (checkLoading(type, id)) {
      return (
        <Button customSize="medium" width={150} appearance="white" isLoading>
          Loading
        </Button>
      );
    }

    /** List of elements to render; depending on the type, status and config of the hardware */
    const actionElements: JSX.Element[] = [];

    // All hardware will have a resolve button if it is inactive
    const isInactive = checkStatus(type, id) === 'Inactive';
    if (isInactive) {
      actionElements.push(
        <Button customSize="medium" width={150} appearance="error" onClick={() => setHardwareTrouble(kioskHardware)}>
          Resolve
        </Button>
      );
    }

    if (type === 'printer') {
      // A printer can have test print & restock buttons - both requiring configuration
      const lowStockAlertThresholds: number[] = get(configuration, 'lowStockAlertThresholds', []);
      const restockAmounts: Array<number | string> = get(configuration, 'restockAmounts', []);
      const testTicketTemplateId: string = get(configuration, 'testTicketTemplateId', '');
      const currentStock = get(values, 'currentStock', 0);

      const testTicketTemplate = kiosk.ticketTemplates.find((template) => template.id === testTicketTemplateId);
      const lowStockThresholdReached =
        lowStockAlertThresholds.length && lowStockAlertThresholds.every((threshold) => currentStock <= threshold);

      const hasTestPrintButton = testTicketTemplate && testTicketTemplate.template ? true : false;
      const hasRestockButton = restockAmounts.length > 0;

      // Show the test print button if a template is configured and the printer is not inactive
      if (hasTestPrintButton && !isInactive) {
        actionElements.push(
          <Button
            customSize="medium"
            width={150}
            appearance="white"
            onClick={() => testPrint((testTicketTemplate as TicketTemplate).template, id)}
          >
            Test Print
          </Button>
        );
      }

      // Show the restock button if restock amounts are configured, even if the printer is inactive
      if (hasRestockButton) {
        actionElements.push(
          <Button
            customSize="medium"
            width={150}
            appearance={lowStockThresholdReached ? 'error' : 'white'}
            onClick={() => setKioskHardwareIdForRestock(id)}
          >
            {`Restock (${currentStock})`}
          </Button>
        );
      }
    } else if (type === 'barcodeScanner' && !isInactive) {
      // A scanner will have a test scan button, if it is not inactive
      const {
        loading,
        status: { message }
      } = scanner;

      if (loading) {
        actionElements.push(<Typography>Please Scan Now</Typography>);
      } else if (!loading && message) {
        actionElements.push(<Typography>{message}</Typography>);
      } else {
        actionElements.push(
          <Button customSize="medium" width={150} appearance="white" onClick={scannerRead}>
            Test Scan
          </Button>
        );
      }
    }

    return (
      <FlexBox direction="row" justify="between" align="center">
        {actionElements.length ? actionElements : '--'}
      </FlexBox>
    );
  };

  const getColumns = () => {
    const columns = [
      {
        header: 'Status',
        cell: (row: KioskHardware) => <HardwareStatus type={row.type} kioskHardwareId={row.id} />,
        width: 80
      },
      {
        header: 'Type',
        cell: (row: KioskHardware) => (
          <Typography fontSize={16} fontWeight={600}>
            {getHardwareDisplayName(row)}
          </Typography>
        )
      },
      {
        header: 'Serial Number',
        show: false,
        cell: (row: KioskHardware) => <Typography>{orDash(row.serialCode)}</Typography>
      },
      {
        header: 'Actions',
        cell: (row: KioskHardware) => <HardwareActionsColumn kioskHardware={row} />,
        width: 370
      }
    ];

    return columns;
  };

  return (
    <MuiBox data-testid="kioskHardwareTable" className={classes.hardwareTableMaintenance}>
      <Card raised fullHeight hasBorderRadius>
        <FlexBox direction="column" noWrap flexOverride="1" fullHeight padding="0 5 4 0">
          <FlexBox direction="row" justify="between" padding="4 0 4 5">
            <FlexBox>
              <Typography fontSize={24} fontWeight={600}>
                Kiosk Status
              </Typography>
            </FlexBox>
            <FlexBox align="center" gutter={3}>
              <FlexBox>
                <Switch
                  checked={kiosk.status === 'Active'}
                  onChange={props.statusChange}
                  data-testid="kiosk-status-switch"
                  checkedLabel="ACTIVE"
                  uncheckedLabel="INACTIVE"
                />
              </FlexBox>
            </FlexBox>
          </FlexBox>
          <Scrollbar>
            <FlexBox direction="column" gutter={3} noWrap flexOverride="1">
              <Table data={hardwareDevices || []} rowHeight={100} isLoading={props.loading} columns={getColumns()} />
            </FlexBox>
          </Scrollbar>
        </FlexBox>
      </Card>

      {hardwareTrouble && (
        <ActionOverlay
          title="The system is experiencing an issue."
          description="Please contact your admin at this time for support. It is recommended you take the kiosk inactive while this issue is resolved."
          cancelText="Close"
          confirmText="Retest"
          onConfirm={() => onRetestHardware()}
          onCancel={() => setHardwareTrouble(null)}
        />
      )}

      {kioskHardwareIdForRestock && (
        <TicketStockOverlay
          onCancel={() => setKioskHardwareIdForRestock(null)}
          onConfirm={() => setKioskHardwareIdForRestock(null)}
          kioskHardwareId={kioskHardwareIdForRestock}
        />
      )}
    </MuiBox>
  );
};
